import {
  decorateEmptyGridValues,
} from "../../../../utils/table/gridUtils";
import {
  FETCH_AFFILIATE_INVOICE_LIST,
  FETCH_AFFILIATE_INVOICE_INIT,
  GENERATE_AFFILIATE_INVOICE,
  DOWNLOAD_AFFILIATE_INVOICE,
  UPDATE_AFFILIATE_INVOICE,
  FETCH_AFFILIATE_INVOICE_PAYMENTS,
  RECORD_AFFILIATE_PAYMENT,
  UPDATE_AFFILIATE_PAYMENT,
  GET_AFFILIATE_INVOICE_DETAIL,
  TOAST,
  SET_AFFILIATE_INVOICE_FILTERS,
  CLEAR_AFFILIATE_INVOICE_FILTERS,
  SAVE_AFFILIATE_INVOICE_TABLE_STATE,
} from "../../../types/affiliateInvoice.type";

const formFieldValueMap = {
  id: null,
  affiliateName: "",
  startDate: null,
  endDate: null,
  invoiceDate: null,
  dueDate: null,
  invoiceStatus: "",
  totalCommissionAmount: "",
  confirmedReservationCount: "",
  confirmedReservationAmount: "",
  cancelledReservationCount: "",
  cancelledReservationAmount: "",
  noshowReservationCount: "",
  noshowReservationAmount: "",
  additionalInfo: "",
  createdDate: "",
  createdBy: "",
  modifiedDate: "",
  modifiedBy: "",
};

const formatTimestamp = (value) => {
  if (!value) return "";
  const num = Number(value);
  if (!isNaN(num) && num > 1000000000) {
    return new Date(num * 1000).toLocaleString();
  }
  return String(value);
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return isNaN(num) ? String(value) : `$${num.toFixed(2)}`;
};

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const INITIAL_STATE = {
  affiliateInvoiceListview: [],
  isLoading: false,
  isLoadingPage: false,
  isGenerateInvoiceSuccess: false,
  isGenerateInvoiceError: false,
  isUpdateInvoiceSuccess: false,
  isUpdateInvoiceError: false,
  isRecordPaymentSuccess: false,
  isRecordPaymentError: false,
  isUpdatePaymentSuccess: false,
  isUpdatePaymentError: false,
  error: null,
  paginationInfo: null,
  isPageLevelError: false,
  invoiceFilters: {
    affiliateId: [],
    invoiceStatus: [],
    invoiceDateStart: null,
    invoiceDateEnd: null,
    dueDateStart: null,
    dueDateEnd: null,
  },
  savedTableState: null,
  invoicePayments: {},
  affiliateInvoiceDetail: null,
  formFieldValueMap,
  isDetailLoading: false,
  isDetailSuccess: false,
  isDetailError: false,
};

const getStatusBadgeClass = (status) => {
  const statusMap = {
    draft: "p-badge-secondary",
    sent: "p-badge-info",
    void: "p-badge-danger",
    paid: "p-badge-success",
    partial_paid: "p-badge-warning",
  };
  return statusMap[status] || "p-badge-secondary";
};

const transformToGridView = (data) => {
  const rows = data.map((item) => {
    const {
      id,
      affiliateName,
      startDate,
      endDate,
      invoiceDate,
      dueDate,
      totalCommissionAmount,
      confirmedReservationCount,
      cancelledReservationCount,
      noshowReservationCount,
      totalPaidAmount,
      invoiceStatus,
      createdDate,
    } = item;

    const transformedValues = {
      id,
      affiliateName,
      startDate,
      endDate,
      invoiceDate,
      dueDate,
      totalCommissionAmount: totalCommissionAmount ? `$${totalCommissionAmount.toFixed(2)}` : "-",
      confirmedReservationCount: confirmedReservationCount || 0,
      cancelledReservationCount: cancelledReservationCount || 0,
      noshowReservationCount: noshowReservationCount || 0,
      totalPaidAmount: totalPaidAmount ? `$${totalPaidAmount.toFixed(2)}` : "-",
      invoiceStatus,
      invoiceStatus,
      createdDate,
    };

    const decoratedValues = {};
    Object.keys(transformedValues).forEach((key) => {
      decoratedValues[key] = decorateEmptyGridValues(transformedValues[key]);
    });
    return decoratedValues;
  });

  const columns = [
    {
      colLabel: "Invoice ID",
      dataKey: "id",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Affiliate Name",
      dataKey: "affiliateName",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Start Date",
      dataKey: "startDate",
      extraProps: {
        isSortable: true,
        isDateField: true,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "End Date",
      dataKey: "endDate",
      extraProps: {
        isSortable: true,
        isDateField: true,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Invoice Date",
      dataKey: "invoiceDate",
      extraProps: {
        isSortable: true,
        isDateField: true,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Due Date",
      dataKey: "dueDate",
      extraProps: {
        isSortable: true,
        isDateField: true,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Status",
      dataKey: "invoiceStatus",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
        isBadge: true,
      },
    },
    {
      colLabel: "Total Commission Amount",
      dataKey: "totalCommissionAmount",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Total Paid Amount",
      dataKey: "totalPaidAmount",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Created Date",
      dataKey: "createdDate",
      extraProps: {
        isSortable: true,
        isDateField: true,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Confirmed Reservations",
      dataKey: "confirmedReservationCount",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Cancelled Reservations",
      dataKey: "cancelledReservationCount",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "No Show Reservations",
      dataKey: "noshowReservationCount",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
  ];

  return {
    datas: { columns, rows },
  };
};

export const affiliateInvoiceReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_AFFILIATE_INVOICE_INIT:
      return {
        ...INITIAL_STATE,
        invoiceFilters: state.invoiceFilters,
        savedTableState: state.savedTableState,
        isLoadingPage: false,
      };

    case FETCH_AFFILIATE_INVOICE_LIST.START:
    case GENERATE_AFFILIATE_INVOICE.START:
    case DOWNLOAD_AFFILIATE_INVOICE.START:
    case UPDATE_AFFILIATE_INVOICE.START:
      return { ...state, isLoading: true };

    case FETCH_AFFILIATE_INVOICE_LIST.SUCCESS: {
      const { page, data } = action.payload;

      let resetState = {
        affiliateInvoiceListview: [],
        paginationInfo: null,
        error: null,
        isLoading: false,
        isLoadingPage: false,
        isPageLevelError: false,
        invoiceFilters: state.invoiceFilters,
        savedTableState: state.savedTableState,
        invoicePayments: state.invoicePayments,
      };

      if (!data) {
        return resetState;
      }

      const { number, size, totalElements, totalPages } = page;
      const { datas } = transformToGridView(data);

      return {
        ...resetState,
        affiliateInvoiceListview: datas,
        paginationInfo: {
          pageSize: size,
          totalRecords: totalElements,
          totalPages: totalPages,
          pageNumber: number,
        },
      };
    }

    case FETCH_AFFILIATE_INVOICE_LIST.ERROR: {
      const { error } = action.payload;

      return {
        ...state,
        affiliateInvoiceListview: { columns: [], rows: [] },
        isLoading: false,
        isLoadingPage: false,
        error,
        isPageLevelError: true,
      };
    }

    case SET_AFFILIATE_INVOICE_FILTERS:
      return {
        ...state,
        invoiceFilters: { ...state.invoiceFilters, ...action.payload },
      };

    case CLEAR_AFFILIATE_INVOICE_FILTERS:
      return {
        ...state,
        invoiceFilters: {
          affiliateId: [],
          invoiceStatus: [],
          invoiceDateStart: null,
          invoiceDateEnd: null,
          dueDateStart: null,
          dueDateEnd: null,
        },
      };

    case SAVE_AFFILIATE_INVOICE_TABLE_STATE:
      return {
        ...state,
        savedTableState: action.payload,
      };

    case GENERATE_AFFILIATE_INVOICE.SUCCESS:
      return {
        ...state,
        isLoading: false,
        isGenerateInvoiceSuccess: true,
        isGenerateInvoiceError: false,
      };

    case GENERATE_AFFILIATE_INVOICE.ERROR: {
      const { error } = action.payload;
      return {
        ...state,
        error,
        isLoading: false,
        isGenerateInvoiceSuccess: false,
        isGenerateInvoiceError: true,
      };
    }

    case DOWNLOAD_AFFILIATE_INVOICE.SUCCESS:
      return {
        ...state,
        isLoading: false,
      };

    case DOWNLOAD_AFFILIATE_INVOICE.ERROR: {
      const { error } = action.payload;
      return {
        ...state,
        error,
        isLoading: false,
      };
    }

    case UPDATE_AFFILIATE_INVOICE.SUCCESS:
      return {
        ...state,
        isLoading: false,
        isUpdateInvoiceSuccess: true,
        isUpdateInvoiceError: false,
      };

    case UPDATE_AFFILIATE_INVOICE.ERROR: {
      const { error } = action.payload;
      return {
        ...state,
        error,
        isLoading: false,
        isUpdateInvoiceSuccess: false,
        isUpdateInvoiceError: true,
      };
    }

    case TOAST.RESET:
      return {
        ...state,
        isGenerateInvoiceSuccess: false,
        isGenerateInvoiceError: false,
        isUpdateInvoiceSuccess: false,
        isUpdateInvoiceError: false,
        isRecordPaymentSuccess: false,
        isRecordPaymentError: false,
        isUpdatePaymentSuccess: false,
        isUpdatePaymentError: false,
      };

    case RECORD_AFFILIATE_PAYMENT.START:
    case UPDATE_AFFILIATE_PAYMENT.START:
      return { ...state, isLoading: true };

    case RECORD_AFFILIATE_PAYMENT.SUCCESS:
      return {
        ...state,
        isLoading: false,
        isRecordPaymentSuccess: true,
        isRecordPaymentError: false,
      };

    case RECORD_AFFILIATE_PAYMENT.ERROR: {
      const { error } = action.payload;
      return {
        ...state,
        error,
        isLoading: false,
        isRecordPaymentSuccess: false,
        isRecordPaymentError: true,
      };
    }

    case FETCH_AFFILIATE_INVOICE_PAYMENTS.START: {
      const { invoiceId } = action.payload;
      return {
        ...state,
        invoicePayments: {
          ...state.invoicePayments,
          [invoiceId]: {
            data: state.invoicePayments[invoiceId]?.data || [],
            isLoading: true,
            error: null,
          },
        },
      };
    }

    case FETCH_AFFILIATE_INVOICE_PAYMENTS.SUCCESS: {
      const { invoiceId, data } = action.payload;
      
      // Store invoice summary data
      const invoiceSummary = {
        totalCommissionAmount: data?.totalCommissionAmount || 0,
        paidAmt: data?.paidAmt || 0,
        unpaidAmt: data?.unpaidAmt || 0,
      };
      const transformPaymentData = (paymentsData) => {
        const payments = Array.isArray(paymentsData) ? paymentsData : paymentsData?.payments || [];
        
        if (!payments || payments.length === 0) {
          return { columns: [], rows: [] };
        }

        const rows = payments.map((payment) => ({
          id: payment.id,
          paidAmount: payment.paidAmount,
          paymentDate: payment.paymentDate,
          paymentMethod: payment.paymentMethod,
          paymentStatus: payment.paymentStatus,
          additionalInfo: payment.additionalInfo,
          createdBy: payment.createdBy,
          createdDate: payment.createdDate,
        }));

        const columns = [
          {
            colLabel: "Payment ID",
            dataKey: "id",
            extraProps: {
              isSortable: false,
              isDateField: false,
              shouldFilter: false,
              hidden: false,
            },
          },
          {
            colLabel: "Paid Amount",
            dataKey: "paidAmount",
            extraProps: {
              isSortable: false,
              isDateField: false,
              shouldFilter: false,
              hidden: false,
            },
          },
          {
            colLabel: "Payment Date",
            dataKey: "paymentDate",
            extraProps: {
              isSortable: false,
              isDateField: true,
              shouldFilter: false,
              hidden: false,
              strategy: "DATE_ONLY",
            },
          },
          {
            colLabel: "Payment Method",
            dataKey: "paymentMethod",
            extraProps: {
              isSortable: false,
              isDateField: false,
              shouldFilter: false,
              hidden: false,
            },
          },
          {
            colLabel: "Status",
            dataKey: "paymentStatus",
            extraProps: {
              isSortable: false,
              isDateField: false,
              shouldFilter: false,
              hidden: false,
              isBadge: true,
            },
          },
          {
            colLabel: "Additional Info",
            dataKey: "additionalInfo",
            extraProps: {
              isSortable: false,
              isDateField: false,
              shouldFilter: false,
              hidden: false,
            },
          },
          {
            colLabel: "Created By",
            dataKey: "createdBy",
            extraProps: {
              isSortable: false,
              isDateField: false,
              shouldFilter: false,
              hidden: false,
            },
          },
          {
            colLabel: "Created Date",
            dataKey: "createdDate",
            extraProps: {
              isSortable: false,
              isDateField: true,
              shouldFilter: false,
              hidden: false,
              strategy: "DATE_ONLY",
            },
          },
        ];

        return { columns, rows };
      };

      const transformedData = transformPaymentData(data || data?.payments || []);

      console.log('Payment Data Received:', data);
      console.log('Transformed Data:', transformedData);

      return {
        ...state,
        invoicePayments: {
          ...state.invoicePayments,
          [invoiceId]: {
            columns: transformedData.columns,
            rows: transformedData.rows,
            summary: invoiceSummary,
            paginationInfo: data?.page || null,
            isLoading: false,
            error: null,
          },
        },
      };
    }

    case FETCH_AFFILIATE_INVOICE_PAYMENTS.ERROR: {
      const { invoiceId, error } = action.payload;
      return {
        ...state,
        invoicePayments: {
          ...state.invoicePayments,
          [invoiceId]: {
            data: [],
            isLoading: false,
            error,
          },
        },
      };
    }

    case UPDATE_AFFILIATE_PAYMENT.SUCCESS:
      return {
        ...state,
        isLoading: false,
        isUpdatePaymentSuccess: true,
        isUpdatePaymentError: false,
      };

    case UPDATE_AFFILIATE_PAYMENT.ERROR: {
      const { error } = action.payload;
      return {
        ...state,
        error,
        isLoading: false,
        isUpdatePaymentSuccess: false,
        isUpdatePaymentError: true,
      };
    }

    case GET_AFFILIATE_INVOICE_DETAIL.START:
      return {
        ...state,
        isDetailLoading: true,
        isDetailSuccess: false,
        isDetailError: false,
      };

    case GET_AFFILIATE_INVOICE_DETAIL.SUCCESS: {
      const temp = { ...formFieldValueMap };
      if (action.payload && typeof action.payload === "object") {
        Object.keys(action.payload).forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(formFieldValueMap, key)) {
            const value = action.payload[key];
            if (key === "startDate" || key === "endDate" || key === "invoiceDate" || key === "dueDate") {
              if (value) {
                const [year, month, day] = value.split("-");
                temp[key] = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              } else {
                temp[key] = null;
              }
            } else if (key === "totalCommissionAmount" || key === "confirmedReservationAmount" || key === "cancelledReservationAmount" || key === "noshowReservationAmount") {
              const num = Number(value);
              temp[key] = (value === null || value === undefined || value === "") ? "" : isNaN(num) ? String(value) : `$${num.toFixed(2)}`;
            } else if (key === "createdDate" || key === "modifiedDate") {
              if (!value) {
                temp[key] = "";
              } else {
                const num = Number(value);
                temp[key] = (!isNaN(num) && num > 1000000000) ? new Date(num * 1000).toLocaleString() : String(value);
              }
            } else {
              temp[key] = value;
            }
          }
        });
      }
      return {
        ...state,
        affiliateInvoiceDetail: action.payload,
        formFieldValueMap: temp,
        isDetailLoading: false,
        isDetailSuccess: true,
        isDetailError: false,
      };
    }

    case GET_AFFILIATE_INVOICE_DETAIL.ERROR:
      return {
        ...state,
        affiliateInvoiceDetail: null,
        formFieldValueMap,
        isDetailLoading: false,
        isDetailSuccess: false,
        isDetailError: true,
        error: action.payload,
      };

    default:
      return state;
  }
};
